/**
 * useAiTaskLaunchDrain — sends prompts parked by `openAiChatWithTask`.
 *
 * The real `sendMessage` only exists inside a tab's React scope
 * (`useAiChatSession`), so the tab has to pull. Mounted once per query tab.
 *
 * Two properties this hook must keep:
 * - **Reads the queue as a store snapshot**, not an event. A launcher can
 *   enqueue for a tab it just created, before that tab has mounted; the
 *   selector read at mount picks it up.
 * - **One run at a time.** `sendMessage` has no concurrency guard, so draining
 *   while the tab is already talking to the CLI would start two overlapping
 *   runs on one session. The next entry waits for the phase to leave `running`.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useAiChatStore } from '../store/aiChatStore';
import { useAiTaskLaunchStore } from '../store/aiTaskLaunchStore';

export type AiTaskLaunchDrainOptions = {
  tabId: string;
  sendMessage: (message: string) => Promise<void>;
};

export function useAiTaskLaunchDrain({ tabId, sendMessage }: AiTaskLaunchDrainOptions): void {
  const next = useAiTaskLaunchStore(useCallback((s) => s.queues[tabId]?.[0], [tabId]));
  const dequeue = useAiTaskLaunchStore((s) => s.dequeue);
  const clearTab = useAiTaskLaunchStore((s) => s.clearTab);
  const phase = useAiChatStore(useCallback((s) => s.getTabState(tabId).phase, [tabId]));
  const isLoading = useAiChatStore(useCallback((s) => s.getTabState(tabId).isLoading, [tabId]));

  // Read through a ref so a new `sendMessage` identity cannot re-fire the effect.
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;

  // Entry ids already handed to `sendMessage`. Never cleared: ids are unique and
  // a stale one can only ever suppress a duplicate send.
  const sentIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!next) return;
    if (phase === 'running' || isLoading) return;
    if (sentIdsRef.current.has(next.id)) return;

    sentIdsRef.current.add(next.id);
    dequeue(tabId, next.id);
    void sendMessageRef.current(next.prompt);
  }, [next, phase, isLoading, dequeue, tabId]);

  // Tab closed — nothing left can ever be drained.
  useEffect(() => () => clearTab(tabId), [clearTab, tabId]);
}
